import React from 'react';
import { FaPrint, FaTimes } from 'react-icons/fa';

const money = (value, currency) => {
  const n = Number(value || 0);
  const formatted = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${formatted}` : formatted;
};

const qty = (value) => {
  const n = Number(value || 0);
  return Number.isInteger(n) ? String(n) : n.toLocaleString();
};

const cbm = (value) => Number(value || 0).toFixed(4);

/**
 * Printable proforma invoice. Renders the server-generated snapshot in a fixed layout and
 * triggers the browser print dialog (Save as PDF). Print CSS isolates `.proforma-print-area`.
 */
const ProformaInvoicePrint = ({ invoice, onClose }) => {
  if (!invoice) return null;

  const { exporter = {}, buyer = {}, items = [], totals = {}, currency, currencySymbol } = invoice;
  // Currency token shown next to prices — symbol when configured, else the currency name/code.
  const curTok = currencySymbol || currency || '';

  return (
    <div className="proforma-print-backdrop" role="dialog" aria-modal="true">
      <div className="proforma-print-toolbar no-print">
        <span className="proforma-print-title">
          Proforma Invoice&nbsp;
          <strong>{invoice.invoiceNumber}</strong>
          {invoice.documentVersion > 1 && (
            <span className="proforma-version-badge">v{invoice.documentVersion}</span>
          )}
        </span>
        <div className="proforma-print-toolbar-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
            <FaPrint className="me-1" /> Print / Save as PDF
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            <FaTimes className="me-1" /> Close
          </button>
        </div>
      </div>

      <div className="proforma-print-scroll">
        <div className="proforma-print-area" id="proforma-print-area">
          {/* Top meta row */}
          <div className="pf-meta-row">
            <div>
              <span className="pf-meta-label">Date:</span> {invoice.date}
              {invoice.buyerOrderNo && (
                <>
                  <span className="pf-meta-label ms-3">Buyer Order No.:</span> {invoice.buyerOrderNo}
                </>
              )}
            </div>
            <div className="text-end">
              <div>
                <span className="pf-meta-label">Proforma Invoice</span>{' '}
                <strong>{invoice.invoiceNumber}</strong>
              </div>
              <div className="pf-meta-version">Version: v{invoice.documentVersion || 1}</div>
            </div>
          </div>

          {/* Exporter / Buyer / Consignee */}
          <table className="pf-parties">
            <tbody>
              <tr>
                <td style={{ width: '38%' }}>
                  {exporter.logo && (
                    <img src={exporter.logo} alt={exporter.companyName || 'Company logo'} className="pf-exporter-logo" />
                  )}
                  <div className="pf-cell-title">EXPORTER</div>
                  <div className="pf-party-name">{exporter.companyName || exporter.label || '—'}</div>
                  <div className="pf-party-text">{exporter.address}</div>
                  {exporter.contactNo1 && <div className="pf-party-text">Tel: {exporter.contactNo1}</div>}
                  {exporter.email && <div className="pf-party-text">Email: {exporter.email}</div>}
                  {exporter.gstNo && <div className="pf-party-text">GST: {exporter.gstNo}</div>}
                  {exporter.regCin && <div className="pf-party-text">CIN: {exporter.regCin}</div>}
                </td>
                <td style={{ width: '31%' }}>
                  <div className="pf-cell-title">BUYER</div>
                  <div className="pf-party-name">{buyer.name || '—'}</div>
                  <div className="pf-party-text">{buyer.address}</div>
                  {buyer.email && <div className="pf-party-text">Email: {buyer.email}</div>}
                  {buyer.gstNo && <div className="pf-party-text">GST: {buyer.gstNo}</div>}
                </td>
                <td style={{ width: '31%' }}>
                  <div className="pf-cell-title">CONSIGNEE</div>
                  <div className="pf-party-name">{buyer.consigneeName || buyer.name || '—'}</div>
                  <div className="pf-party-text">{buyer.consigneeAddress || buyer.address}</div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Shipment terms strip. Domestic buyers drop Port of Delivery, fix Country of Receipt to
              India, and surface Payment Terms; export buyers show the configured Buyer Ledger values. */}
          <table className="pf-terms">
            <tbody>
              {buyer.isDomestic ? (
                <tr>
                  <td><span className="pf-meta-label">Place of Receipt:</span> {buyer.placeOfReceipt || '—'}</td>
                  <td><span className="pf-meta-label">Country of Receipt:</span> {buyer.countryOfReceipt || 'India'}</td>
                  <td><span className="pf-meta-label">Payment Terms:</span> {buyer.paymentTerm || '—'}</td>
                  <td><span className="pf-meta-label">Currency:</span> {curTok || '—'}</td>
                </tr>
              ) : (
                <>
                  <tr>
                    <td><span className="pf-meta-label">Place of Receipt:</span> {buyer.placeOfReceipt || '—'}</td>
                    <td><span className="pf-meta-label">Port of Delivery:</span> {buyer.portOfDelivery || '—'}</td>
                    <td><span className="pf-meta-label">Country of Receipt:</span> {buyer.countryOfReceipt || '—'}</td>
                    <td><span className="pf-meta-label">Currency:</span> {curTok || '—'}</td>
                  </tr>
                  <tr>
                    <td><span className="pf-meta-label">Payment Terms:</span> {buyer.paymentTerm || '—'}</td>
                    <td><span className="pf-meta-label">Notify Party:</span> {buyer.notifyParty || '—'}</td>
                    <td colSpan={2}><span className="pf-meta-label">Also Notify:</span> {buyer.alsoNotifyParty || '—'}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          {/* Item table */}
          <table className="pf-items">
            <thead>
              <tr>
                <th>S.No</th>
                <th>P code</th>
                <th>Item No</th>
                <th>Image</th>
                <th>Description</th>
                <th>Inner Qty</th>
                <th>Master Qty</th>
                <th>Client SKU</th>
                <th>Barcode</th>
                <th>Price</th>
                <th>Cartons</th>
                <th>CBM/Carton</th>
                <th>Total CBM</th>
                <th>Order Qty</th>
                <th>Total Qty</th>
                <th>Total Price</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.productUid || index}>
                  <td>{index + 1}</td>
                  <td>{item.pCode || '—'}</td>
                  <td>{item.itemNo || '—'}</td>
                  <td className="pf-img-cell">
                    {item.image ? (
                      <img src={item.image} alt={item.itemNo || item.pCode} className="pf-item-img" />
                    ) : (
                      <span className="pf-muted">—</span>
                    )}
                  </td>
                  <td className="pf-desc-cell">
                    <div className="pf-desc-name">{item.description || '—'}</div>
                    {item.color && <div className="pf-desc-size">Color: {item.color}</div>}
                    {item.size && <div className="pf-desc-size">{item.size}</div>}
                  </td>
                  <td>{item.innerPack || '—'}</td>
                  <td>{item.masterPack || '—'}</td>
                  <td>{item.clientSku || '—'}</td>
                  <td>{item.barcode || '—'}</td>
                  <td className="text-end">{money(item.price, curTok)}</td>
                  <td className="text-end">{qty(item.cartons)}</td>
                  <td className="text-end">{cbm(item.cbmPerCarton)}</td>
                  <td className="text-end">{cbm(item.totalCbm)}</td>
                  <td className="text-end">{qty(item.orderQty)}</td>
                  <td className="text-end">{qty(item.totalQty)}</td>
                  <td className="text-end">{money(item.totalPrice, curTok)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={10} className="text-end pf-total-label">Total</td>
                <td className="text-end">{qty(totals.cartons)}</td>
                <td></td>
                <td className="text-end">{cbm(totals.cbm)}</td>
                <td></td>
                <td className="text-end">{qty(totals.qty)}</td>
                <td className="text-end">{money(totals.amount, curTok)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Beneficiary footer — the exporter is the payment beneficiary. */}
          <table className="pf-beneficiary">
            <tbody>
              <tr>
                <td>
                  <div className="pf-cell-title">BENEFICIARY</div>
                  <div className="pf-party-name">{exporter.companyName || exporter.label || '—'}</div>
                  <div className="pf-party-text">{exporter.address || '—'}</div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="pf-footnote">
            This is a system-generated proforma invoice. Values are AI-assisted drafts — validate before acting.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProformaInvoicePrint;
